import React from 'react'
import { Box, Typography } from '@mui/material'
import { VideoView } from '@whereby.com/browser-sdk/react'
import type {
  LocalParticipant as WherebyLocalParticipant,
  RemoteParticipant as WherebyRemoteParticipant,
  Screenshare as WherebyScreenshare
} from '@whereby.com/browser-sdk/react'
import {
  getWherebyParticipantDisplayName,
  isWherebyBotParticipant
} from '../../../../../../pure-js/libs/isWherebyBotParticipant'
import { ParticipantsPanel } from './ParticipantsPanel'
import { ToolbarButton } from './ToolbarButton'
import type { ToolbarButtonViewModel } from './ToolbarButton'
import { WherebyScreenshareStage } from './WherebyScreenshareStage'
import { buildPeopleGridSx, buildVideoAreaSx } from './wherebyCallStyles'
import { hasScreenshareStream } from './wherebyCallHelpers'
import { INTERAX_COLORS } from '../../../../constants/colors'

export interface ParticipantListItem {
  id: string
  userId: string | null
  name: string
}

export interface WherebyVideoAreaProps {
  compact: boolean
  displayName: string
  localParticipant?: WherebyLocalParticipant
  remoteParticipants: WherebyRemoteParticipant[]
  screenshares?: WherebyScreenshare[]
  anonymousName: string
  showParticipants: boolean
  participantList: ParticipantListItem[]
  participantsTitle: string
  toolbarItems: ToolbarButtonViewModel[]
  onSettingsOpen: (element: HTMLElement) => void
}

const renderParticipantPlaceholderTile = (name: string, key: React.Key) => (
  <Box
    key={key}
    sx={{
      minHeight: 0,
      borderRadius: '8px',
      backgroundColor: INTERAX_COLORS.black55,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: { xs: 1, sm: 1.5, md: 2 }
    }}
  >
    <Typography
      sx={{
        color: INTERAX_COLORS.white70,
        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {name}
    </Typography>
  </Box>
)

const renderLocalParticipantTile = (
  displayName: string,
  localParticipant?: WherebyLocalParticipant
): React.ReactNode => {
  if (!localParticipant) return null
  const participantId = localParticipant.id ?? 'local'
  if (localParticipant.stream) {
    const videoTrackId = localParticipant.stream.getVideoTracks()[0]?.id ?? localParticipant.stream.id
    return (
      <VideoView
        key={`${participantId}:${localParticipant.stream.id}:${videoTrackId}`}
        stream={localParticipant.stream}
        muted={true}
        style={{ objectFit: 'cover' }}
      />
    )
  }
  return renderParticipantPlaceholderTile(displayName, participantId)
}

const renderToolbar = (
  toolbarItems: ToolbarButtonViewModel[],
  compact: boolean,
  onSettingsOpen: (element: HTMLElement) => void
) =>
  toolbarItems.map(({ id, Icon, MutedIcon, label, onClick, isActive, variant }) => (
    <ToolbarButton
      key={id}
      id={id}
      Icon={Icon}
      MutedIcon={MutedIcon}
      label={label}
      onClick={onClick}
      onSettingsOpen={onSettingsOpen}
      isActive={isActive}
      variant={variant}
      compact={compact}
    />
  ))

export const WherebyVideoArea: React.FC<WherebyVideoAreaProps> = ({
  compact,
  displayName,
  localParticipant,
  remoteParticipants,
  screenshares = [],
  anonymousName,
  showParticipants,
  participantList,
  participantsTitle,
  toolbarItems,
  onSettingsOpen
}) => {
  const sharedScreens = screenshares.filter(hasScreenshareStream)
  const hasSharedScreen = sharedScreens.length > 0
  const isScreenSharedOnPage = hasSharedScreen && !compact

  return (
    <>
      <Box sx={buildVideoAreaSx(compact, isScreenSharedOnPage)}>
        {hasSharedScreen ? <WherebyScreenshareStage screenshares={sharedScreens} /> : null}

        <Box sx={buildPeopleGridSx(compact, isScreenSharedOnPage)}>
          {renderLocalParticipantTile(displayName, localParticipant)}
          {remoteParticipants.map((participant, index) => {
            const name = getWherebyParticipantDisplayName(participant, anonymousName)
            if (isWherebyBotParticipant({ roleName: participant.roleName, displayName: name })) return null
            const key = participant.id ?? `remote-${index}`
            return participant.stream ? (
              <VideoView key={key} stream={participant.stream} style={{ objectFit: 'cover' }} />
            ) : (
              renderParticipantPlaceholderTile(name, key)
            )
          })}
        </Box>

        {showParticipants ? <ParticipantsPanel participants={participantList} title={participantsTitle} /> : null}

        {compact ? (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: INTERAX_COLORS.black55,
              borderRadius: '999px',
              padding: '5px 8px',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              zIndex: 2
            }}
          >
            {renderToolbar(toolbarItems, true, onSettingsOpen)}
          </Box>
        ) : null}
      </Box>

      {compact ? null : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            flexShrink: 0
          }}
        >
          {renderToolbar(toolbarItems, false, onSettingsOpen)}
        </Box>
      )}
    </>
  )
}
